// IM-EXT-015 (IM-UI-071) — Battlecards: um por concorrente CONFIRMADO, e a visão de posicionamento competitivo.
// Hipótese de concorrente aparece com o motivo de não ter battlecard (decisão humana antes). Informação prioritária
// primeiro; detalhes recolhidos.
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { POSITIONING_ROLE_LABEL, type BattlecardListItemDto, type PositioningDto } from '@intelimove/contracts';
import { ModuleIntro } from '../components/Guidance';
import { AsyncView, EmptyState } from '../components/States';
import { useToast } from '../components/Toast';
import { PageHead, StatusPill } from '../components/Widgets';
import { battlecardApi, CLASS_SHORT, CLASS_TONE, fmtDate, LEVEL_TXT } from '../data/battlecards';
import { BATTLECARD_GUIDE } from '../data/guides';
import { useResource } from '../hooks/useResource';
import { useCanManage } from './Targets';

const TABS: Array<[string, string]> = [['lista', 'Battlecards'], ['posicionamento', 'Posicionamento competitivo']];

export function Battlecards() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('aba') === 'posicionamento' ? 'posicionamento' : 'lista';
  const go = (k: string) => { const p = new URLSearchParams(params); p.set('aba', k); setParams(p); };
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Concorrentes e mercado" title="Battlecards" subtitle="Como competir com cada concorrente confirmado: o que é fato, o que é declarado pela própria empresa, o que é hipótese — com fonte, data e decisão da sua equipe." />
      <ModuleIntro guide={BATTLECARD_GUIDE} />
      <nav className="tabs" aria-label="Seções de battlecards">
        {TABS.map(([k, label]) => <a key={k} href={`?aba=${k}`} className={tab === k ? 'active' : ''} aria-current={tab === k ? 'page' : undefined} onClick={(e) => { e.preventDefault(); go(k); }}>{label}</a>)}
      </nav>
      {tab === 'lista' ? <List /> : <Positioning />}
    </section>
  );
}

function List() {
  const canManage = useCanManage();
  const toast = useToast();
  const nav = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const { state, reload } = useResource((s) => battlecardApi.list(s), [], (d) => d.items.length === 0);
  const create = async (x: BattlecardListItemDto) => {
    setBusy(x.competitor.id);
    try { const d = await battlecardApi.create(x.competitor.id); toast('Battlecard criado (versão 1). Revise os itens antes de usar com clientes.'); nav(`/battlecards/${d.id}`); }
    catch (e) { toast((e as Error).message, 'error'); setBusy(null); }
  };
  return (
    <AsyncView state={state} onRetry={reload}
      empty={<EmptyState title="Nenhum concorrente confirmado ainda" action={<Link className="cta btn-link" to="/configuracoes/contexto/concorrentes">Ir para Concorrentes</Link>}>
        O battlecard nasce de um concorrente confirmado pela sua equipe. Adicione e confirme concorrentes no Contexto da empresa.</EmptyState>}>
      {(d) => {
        const withCard = d.items.filter((x) => x.battlecard);
        const ready = d.items.filter((x) => !x.battlecard && x.eligible);
        const waiting = d.items.filter((x) => !x.battlecard && !x.eligible);
        return (
          <>
            {withCard.length > 0 && (
              <div className="table-card">
                <div className="table-wrap">
                  <table className="data-table stack">
                    <caption className="sr-only">Battlecards</caption>
                    <thead><tr><th scope="col">Concorrente</th><th scope="col">Papel</th><th scope="col">Versão</th><th scope="col">Revisão</th><th scope="col">Atualizado</th><th scope="col"><span className="sr-only">Ações</span></th></tr></thead>
                    <tbody>
                      {withCard.map((x) => (
                        <tr key={x.competitor.id}>
                          <td data-label="Concorrente"><b>{x.competitor.tradeName ?? x.competitor.name}</b><span className="cell-sub">{x.competitor.domain ?? 'sem site'}</span>
                            {x.competitor.status !== 'CONFIRMED' && <span className="cell-sub merge">Concorrente não está mais confirmado</span>}</td>
                          <td data-label="Papel">{x.battlecard!.role ? POSITIONING_ROLE_LABEL[x.battlecard!.role] : <span className="cell-sub">não definido</span>}</td>
                          <td data-label="Versão">v{x.battlecard!.versionNo}{x.battlecard!.status === 'ARCHIVED' && <span className="cell-sub">arquivado</span>}</td>
                          <td data-label="Revisão">{/* IM-UX-004: destaca só o que exige ação humana (mesma regra da fila do detalhe). */}
                            {x.battlecard!.pending ? <Link to={`/battlecards/${x.battlecard!.id}?aba=revisao`}><StatusPill tone="amber">{x.battlecard!.pending} precisa(m) de revisão</StatusPill></Link> : <StatusPill tone="mint">Nada a revisar</StatusPill>}<span className="cell-sub">{x.battlecard!.inUse} em uso</span></td>
                          <td data-label="Atualizado">{fmtDate(x.battlecard!.updatedAt, true)}</td>
                          <td><Link className="small-action" to={`/battlecards/${x.battlecard!.id}`}>Abrir</Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {ready.length > 0 && (
              <div className="card">
                <h2 className="card-title">Concorrentes confirmados sem battlecard</h2>
                <ul className="entity-list-mini">
                  {ready.map((x) => (
                    <li key={x.competitor.id} className="bc-row">
                      <div><b>{x.competitor.tradeName ?? x.competitor.name}</b><span className="cell-sub">{x.competitor.domain ?? 'sem site'}</span></div>
                      {canManage ? <button className="cta" disabled={busy !== null} onClick={() => void create(x)}>{busy === x.competitor.id ? 'Criando…' : 'Criar battlecard'}</button> : <span className="cell-sub">Um administrador pode criar.</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {waiting.length > 0 && (
              <details className="card details">
                <summary>Hipóteses de concorrente ({waiting.length}) — sem battlecard até a confirmação</summary>
                <ul className="entity-list-mini">
                  {waiting.map((x) => <li key={x.competitor.id}><b>{x.competitor.tradeName ?? x.competitor.name}</b><span className="cell-sub wide">{x.ineligibleReason}</span></li>)}
                </ul>
                <Link className="secondary-link" to="/configuracoes/contexto/concorrentes">Revisar concorrentes</Link>
              </details>
            )}
          </>
        );
      }}
    </AsyncView>
  );
}

const GROUPS: Array<[keyof PositioningDto['groups'], string]> = [['DIRETO', 'Concorrentes diretos'], ['INDIRETO', 'Concorrentes indiretos'], ['SUBSTITUTO', 'Substitutos'], ['NAO_CLASSIFICADO', 'Papel ainda não decidido']];
const LEVEL_PT: Record<string, string> = { ALTA: 'Alta', MEDIA: 'Média', BAIXA: 'Baixa', INDETERMINADA: 'Indeterminada' };

function Positioning() {
  const { state, reload } = useResource((s) => battlecardApi.positioning(s), [], (d) => Object.values(d.groups).every((g) => g.length === 0));
  return (
    <AsyncView state={state} onRetry={reload} empty={<EmptyState title="Sem concorrentes confirmados">O posicionamento compara os concorrentes confirmados com a sua empresa. Confirme concorrentes e crie os battlecards.</EmptyState>}>
      {(p) => {
        const all = Object.values(p.groups).flat();
        const nameOf = (id: string) => all.find((x) => x.competitorId === id)?.name ?? '—';
        return (
          <>
            {p.limitations.length > 0 && <div className="card notice" role="status"><b>Leia com atenção</b><ul className="notes">{p.limitations.map((l) => <li key={l}>{l}</li>)}</ul></div>}
            <div className="bc-groups">
              {GROUPS.map(([k, label]) => (
                <article key={k} className={`card bc-group bc-group-${k.toLowerCase()}`} aria-labelledby={`g-${k}`}>
                  <h2 className="card-title" id={`g-${k}`}>{label} <span className="swot-count">({p.groups[k].length})</span></h2>
                  {p.groups[k].length === 0 ? <p className="cell-sub">Nenhum.</p> : (
                    <ul className="entity-list-mini">
                      {p.groups[k].map((x) => (
                        <li key={x.competitorId}>
                          {x.battlecardId ? <Link to={`/battlecards/${x.battlecardId}`}><b>{x.name}</b></Link> : <b>{x.name}</b>}
                          <span className="cell-sub wide">
                            {x.competitionLevel ? `Grau de concorrência ${LEVEL_TXT[x.competitionLevel] ?? x.competitionLevel}` : 'Grau não medido'}
                            {!x.roleDecided && x.roleSuggested ? ` · sugestão: ${POSITIONING_ROLE_LABEL[x.roleSuggested].toLowerCase()}` : ''}
                            {` · ${x.threats} risco(s) · ${x.opportunities} oportunidade(s) · ${x.signals90d} sinal(is) em 90 dias`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
            <div className="card">
              <h2 className="card-title">Concentração: {LEVEL_PT[p.concentration.level]}</h2>
              <p className="cell-sub wide">{p.concentration.explanation}</p>
              {p.spaces.length > 0 && (
                <div className="table-wrap">
                  <table className="data-table stack">
                    <caption className="sr-only">Espaços de mercado</caption>
                    <thead><tr><th scope="col">Espaço de mercado</th><th scope="col">Tipo</th><th scope="col">Concorrentes</th><th scope="col">Sua empresa</th><th scope="col">Concentração</th></tr></thead>
                    <tbody>{p.spaces.map((s) => (
                      <tr key={`${s.kind}-${s.label}`}><td data-label="Espaço">{s.label}</td><td data-label="Tipo">{s.kind === 'OFERTA' ? 'Sua oferta' : 'Segmento'}</td><td data-label="Concorrentes">{s.competitors.join(', ') || '—'}</td><td data-label="Sua empresa">{s.tenantPresent ? 'Presente' : 'Ausente'}</td><td data-label="Concentração">{LEVEL_PT[s.concentration]}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
            {p.axes.length > 0 && (
              <details className="card details">
                <summary>Eixos de comparação ({p.axes.length})</summary>
                <div className="table-wrap">
                  <table className="data-table stack">
                    <caption className="sr-only">Eixos de comparação por concorrente</caption>
                    <thead><tr><th scope="col">Eixo</th>{all.filter((x) => p.axes.some((a) => a.values.some((v) => v.competitorId === x.competitorId))).map((x) => <th key={x.competitorId} scope="col">{x.name}</th>)}</tr></thead>
                    <tbody>{p.axes.map((a) => {
                      const cols = all.filter((x) => p.axes.some((ax) => ax.values.some((v) => v.competitorId === x.competitorId)));
                      return <tr key={a.key}><td data-label="Eixo">{a.label}<span className="cell-sub">{a.origin === 'COMPARISON' ? 'sobreposição medida pela pesquisa' : 'dimensão da análise de mercado'}</span></td>{cols.map((x) => <td key={x.competitorId} data-label={x.name}>{a.values.find((v) => v.competitorId === x.competitorId)?.value ?? '—'}</td>)}</tr>;
                    })}</tbody>
                  </table>
                </div>
              </details>
            )}
            <div className="bc-two">
              <div className="card">
                <h2 className="card-title">Diferenciação</h2>
                {p.differentiation.length === 0 ? <p className="cell-sub">Sua empresa ainda não tem diferenciais no contexto.</p> : (
                  <ul className="entity-list-mini">{p.differentiation.map((d) => <li key={d.value}><b>{d.value}</b><span className="cell-sub wide">{d.claimedBy.length ? `Também declarado por: ${d.claimedBy.join(', ')}` : 'Nenhum concorrente declara — espaço de diferenciação.'}</span></li>)}</ul>
                )}
              </div>
              <div className="card">
                <h2 className="card-title">Ameaças</h2>
                <ThreatList list={p.threats} nameOf={nameOf} empty="Nenhuma ameaça registrada nos battlecards." />
                <h2 className="card-title bc-mt">Oportunidades</h2>
                <ThreatList list={p.opportunities} nameOf={nameOf} empty="Nenhuma oportunidade registrada nos battlecards." />
              </div>
            </div>
            <p className="cell-sub">Gerado em {fmtDate(p.generatedAt, true)} a partir das versões atuais dos battlecards{p.contextConfirmed ? ' e do contexto confirmado' : ''}.</p>
          </>
        );
      }}
    </AsyncView>
  );
}

function ThreatList({ list, empty }: { list: PositioningDto['threats']; nameOf: (id: string) => string; empty: string }) {
  if (!list.length) return <p className="cell-sub">{empty}</p>;
  return <ul className="entity-list-mini">{list.map((t, i) => <li key={`${t.competitorId}-${i}`}><span>{t.text}</span> <span className={`tag ${CLASS_TONE[t.classification]}`}>{CLASS_SHORT[t.classification]}</span><span className="cell-sub">{t.name}</span></li>)}</ul>;
}
